import { MAX_CONCURRENT_REQUESTS, PREFETCH_CONCURRENCY_LIMIT } from './constants';
import { createAbortError } from './errors';

interface Job {
    run: () => Promise<unknown>;
    resolve: (value: unknown) => void;
    reject: (reason: unknown) => void;
    signal?: AbortSignal;
}

export default class RequestQueue {
    private visible: Job[] = [];

    private prefetch: Job[] = [];

    private active = 0;

    enqueue<T>(run: () => Promise<T>, prefetch = false, signal?: AbortSignal): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const job: Job = {
                run,
                resolve: resolve as Job['resolve'],
                reject,
                signal,
            };
            (prefetch ? this.prefetch : this.visible).push(job);
            this.pump();
        });
    }

    clear(): void {
        const dropped = [...this.visible, ...this.prefetch];
        this.visible = [];
        this.prefetch = [];
        dropped.forEach((job) => job.reject(createAbortError()));
    }

    get inFlight(): number {
        return this.active;
    }

    private pump(): void {
        while (
            this.active < MAX_CONCURRENT_REQUESTS
            && (this.visible.length || (this.prefetch.length && this.active < PREFETCH_CONCURRENCY_LIMIT))
        ) {
            const job = this.visible.length ? this.visible.shift() : this.prefetch.shift();
            if (job.signal?.aborted) {
                job.reject(createAbortError());
            } else {
                this.active += 1;
                new Promise((resolve) => {
                    resolve(job.run());
                })
                    .then(job.resolve, job.reject)
                    .finally(() => {
                        this.active -= 1;
                        this.pump();
                    });
            }
        }
    }
}
